import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Wine, Shield, CheckCircle, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

// Mapping from server status → which timeline stage is "current" (active) and which are done.
const TIMELINE_STAGES = [
  { id: "submitted", label: "Candidature reçue", short: "Candidature" },
  { id: "approved", label: "Étude & validation", short: "Étude" },
  { id: "invoice_sent", label: "Lien de paiement envoyé", short: "Paiement" },
  { id: "active", label: "Membre actif", short: "Membre" },
];
function stageIndexFromStatus(s) {
  if (["submitted", "interview_scheduled", "interview_completed"].includes(s)) return 0;
  if (["approved", "billing_pending", "billing_received", "invoice_draft"].includes(s)) return 1;
  if (["invoice_sent"].includes(s)) return 2;
  if (["paid", "onboarding_scheduled", "active"].includes(s)) return 3;
  return 0;
}

function CandidacyTimeline({ status }) {
  const current = stageIndexFromStatus(status);
  return (
    <div className="mt-6 rounded-2xl border border-wine-100 bg-white p-5" data-testid="candidacy-timeline">
      <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">Où en est votre candidature ?</div>
      <ol className="mt-4 flex items-start justify-between gap-2">
        {TIMELINE_STAGES.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li key={s.id} className="flex-1 text-center" data-testid={`timeline-step-${i}`}>
              <div className={`mx-auto grid h-9 w-9 place-items-center rounded-full text-xs font-bold ${
                done ? "bg-emerald-500 text-white" : active ? "bg-wine-500 text-white ring-4 ring-wine-100" : "bg-neutral-200 text-neutral-400"
              }`}>{done ? <CheckCircle className="h-5 w-5" /> : i + 1}</div>
              <div className={`mt-2 text-[11px] font-bold ${active ? "text-wine-700" : done ? "text-emerald-700" : "text-neutral-400"}`}>{s.short}</div>
              {active && <div className="mt-1 text-[10px] text-neutral-500 hidden sm:block">{s.label}</div>}
              {i < TIMELINE_STAGES.length - 1 && (
                <div className={`mt-[-22px] mx-auto h-[2px] w-full ${done ? "bg-emerald-300" : "bg-neutral-200"}`} style={{ transform: "translate(50%, 0)" }} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function BillingPage() {
  const { t } = useLang();
  const { appId } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    legal_company_name: "", billing_contact_name: "", billing_email: "",
    vat_number: "", company_registration_number: "",
    billing_address: "", postal_code: "", city: "", country: "France",
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // SIREN lookup (api.gouv.fr public, no key)
  const [sirenQuery, setSirenQuery] = useState("");
  const [sirenLoading, setSirenLoading] = useState(false);
  const [sirenResults, setSirenResults] = useState([]);

  useEffect(() => {
    api.get(`/billing/${appId}`).then((r) => {
      setInfo(r.data);
      setForm((f) => ({
        ...f,
        legal_company_name: r.data.billing_info?.legal_company_name || r.data.company_name,
        billing_contact_name: r.data.billing_info?.billing_contact_name || r.data.contact_name,
        billing_email: r.data.billing_info?.billing_email || r.data.email,
        country: r.data.billing_info?.country || r.data.country || "France",
        vat_number: r.data.billing_info?.vat_number || "",
        company_registration_number: r.data.billing_info?.company_registration_number || "",
        billing_address: r.data.billing_info?.billing_address || "",
        postal_code: r.data.billing_info?.postal_code || "",
        city: r.data.billing_info?.city || "",
      }));
      if (r.data.status === "billing_received" || r.data.status === "invoice_sent" || r.data.status === "paid" || r.data.status === "onboarding_scheduled" || r.data.status === "active") setDone(true);
    }).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Lien invalide"));
  }, [appId]);

  const searchSiren = async () => {
    if (sirenQuery.trim().length < 3) {
      toast.error("Saisissez au moins 3 caractères (SIREN, SIRET ou nom)");
      return;
    }
    setSirenLoading(true);
    try {
      const { data } = await api.get("/public/siren-lookup", { params: { q: sirenQuery.trim() } });
      setSirenResults(data.results || []);
      if (!data.results?.length) toast.info("Aucun résultat — vérifiez votre saisie");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de recherche");
    }
    setSirenLoading(false);
  };

  const applySirenResult = (r) => {
    setForm((f) => ({
      ...f,
      legal_company_name: r.legal_name || f.legal_company_name,
      company_registration_number: r.siren || f.company_registration_number,
      billing_address: r.address || f.billing_address,
      postal_code: r.postal_code || f.postal_code,
      city: r.city || f.city,
      country: r.country || f.country,
    }));
    setSirenResults([]);
    toast.success(`✓ ${r.legal_name} pré-rempli`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/billing/${appId}`, form);
      setDone(true);
      toast.success("Informations transmises ✓");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (error) return (
    <div className="grid min-h-screen place-items-center bg-wine-50/30 px-6">
      <div className="rounded-3xl border border-red-100 bg-white p-10 text-center max-w-md">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-50 via-white to-wine-50/40">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Logo />
        {info && <CandidacyTimeline status={info.status} />}
        {done ? (
          <div className="mt-10 rounded-3xl border border-wine-100 bg-white p-10 text-center" data-testid="billing-success">
            <CheckCircle className="mx-auto h-16 w-16 text-emerald-500" />
            <h1 className="mt-5 text-3xl font-bold text-navy">{t("comp_billingpage_90")}</h1>
            <p className="mt-3 text-neutral-600">{t("comp_billingpage_91")}</p>
            {info?.stripe?.checkout_url && info?.status !== "paid" && info?.status !== "active" && info?.status !== "onboarding_scheduled" ? (
              <>
                <p className="mt-4 text-sm text-neutral-700">
                  💳 Votre facture est prête. Cliquez ci-dessous pour finaliser votre paiement en toute sécurité.
                </p>
                <a
                  href={info.stripe.checkout_url}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-wine-500 px-7 py-3 text-sm font-bold text-white shadow-sm hover:bg-wine-600"
                  data-testid="pay-now-btn"
                >
                  Payer ma cotisation maintenant
                </a>
                {info?.fee_calculation?.final_amount && (
                  <div className="mt-3 text-sm font-semibold text-wine-700">Montant : {info.fee_calculation.final_amount} € HT</div>
                )}
              </>
            ) : info?.status === "paid" || info?.status === "active" || info?.status === "onboarding_scheduled" ? (
              <p className="mt-4 text-sm text-emerald-700 font-semibold">🎉 Votre paiement est bien reçu. Bienvenue chez La WineTech !</p>
            ) : (
              <p className="mt-3 text-sm text-neutral-500">L&apos;équipe La WineTech va générer votre facture et vous l&apos;envoyer par email avec le lien de paiement Stripe sécurisé.</p>
            )}
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-2 rounded-full bg-wine-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-wine-700 w-fit">
              <Shield className="h-3.5 w-3.5" /> Lien sécurisé
            </div>
            <h1 className="mt-4 text-4xl font-bold text-navy md:text-5xl">
              Informations de <span className="font-display italic text-wine-600" style={{ fontWeight: 500 }}>facturation</span>
            </h1>
            <p className="mt-3 max-w-xl text-neutral-700">
              Félicitations <span className="font-semibold">{info?.contact_name}</span> {t("comp_billingpage_92")} <span className="font-semibold">{info?.company_name}</span> est approuvée. Renseignez ci-dessous les informations légales pour générer votre facture d&apos;adhésion.
            </p>

            {/* SIREN public lookup — pre-fills the form from api.gouv.fr */}
            <div className="mt-6 rounded-2xl border border-wine-100 bg-wine-50/40 p-4" data-testid="siren-lookup-block">
              <div className="text-xs font-bold uppercase tracking-wider text-wine-700">⚡ Gagnez du temps</div>
              <p className="mt-1 text-xs text-neutral-600">Saisissez votre SIREN/SIRET ou nom d&apos;entreprise — on pré-remplit le formulaire depuis le registre officiel français.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <input
                  value={sirenQuery}
                  onChange={(e) => setSirenQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchSiren())}
                  placeholder="552120222 ou Société Générale"
                  className="flex-1 min-w-[200px] rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-wine-300"
                  data-testid="siren-input"
                />
                <button type="button" onClick={searchSiren} disabled={sirenLoading}
                        className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white hover:bg-wine-600 disabled:opacity-50"
                        data-testid="siren-search-btn">
                  {sirenLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                  Rechercher
                </button>
              </div>
              {sirenResults.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="siren-results">
                  {sirenResults.map((r) => (
                    <button key={r.siret || r.siren} type="button" onClick={() => applySirenResult(r)}
                            className="w-full rounded-xl border border-neutral-200 bg-white p-3 text-left text-sm hover:border-wine-300 hover:bg-wine-50/30"
                            data-testid={`siren-result-${r.siren}`}>
                      <div className="font-semibold text-navy">{r.legal_name}</div>
                      <div className="text-xs text-neutral-600">{r.address}, {r.postal_code} {r.city} · SIREN {r.siren}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={submit} className="mt-6 grid gap-4 rounded-3xl border border-wine-100 bg-white p-8 md:grid-cols-2">
              <Field2 label="Raison sociale *" v={form.legal_company_name} on={(v) => setForm({ ...form, legal_company_name: v })} test="bill-legal" required />
              <Field2 label="Contact facturation *" v={form.billing_contact_name} on={(v) => setForm({ ...form, billing_contact_name: v })} test="bill-contact" required />
              <Field2 label="Email facturation *" type="email" v={form.billing_email} on={(v) => setForm({ ...form, billing_email: v })} test="bill-email" required />
              <Field2 label={t("comp_billingpage_93")} v={form.vat_number} on={(v) => setForm({ ...form, vat_number: v })} test="bill-vat" placeholder="FR12345678901" />
              <Field2 label="N° SIREN / RCS" v={form.company_registration_number} on={(v) => setForm({ ...form, company_registration_number: v })} test="bill-reg" placeholder="SIREN/RCS" />
              <Field2 label="Pays *" v={form.country} on={(v) => setForm({ ...form, country: v })} test="bill-country" required />
              <div className="md:col-span-2"><Field2 label="Adresse *" v={form.billing_address} on={(v) => setForm({ ...form, billing_address: v })} test="bill-address" required /></div>
              <Field2 label="Code postal *" v={form.postal_code} on={(v) => setForm({ ...form, postal_code: v })} test="bill-postal" required />
              <Field2 label="Ville *" v={form.city} on={(v) => setForm({ ...form, city: v })} test="bill-city" required />
              <div className="md:col-span-2 flex items-center justify-between gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Wine className="h-3.5 w-3.5 text-wine-500" /> Vos informations sont sécurisées
                </div>
                <button type="submit" disabled={loading} className="rounded-full bg-wine-500 px-7 py-3 text-sm font-semibold text-white shadow-sm hover:bg-wine-600 disabled:opacity-50" data-testid="bill-submit">
                  {loading ? "Envoi..." : "Transmettre"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field2({ label, v, on, type = "text", test, required, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
      <input
        type={type}
        required={required}
        value={v}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-wine-300"
        data-testid={test}
      />
    </div>
  );
}
